import { OAUTH_CALLBACK_PATH } from '@/pages/Integrations/oauth-callback-standalone';
import { SIGNUP_PATH } from '@/pages/Signup/signup-standalone';
import { VERIFY_PATH } from '@/pages/Verify/verify-standalone';
import { INVITE_ACCEPT_PATH } from '@/pages/InviteAccept/invite-accept-standalone';
import { parseSigningReturnPath } from '@/pages/SigningReturn/signing-return-path';
import type { SigningReturnOutcome } from '@/lib/contracts/signing-return-message';
import {
  parseDocumentSignatureReturnPath,
  type DocumentSignatureReturnOutcome,
} from '@/pages/DocumentSignatureReturn/document-signature-return-path';
import { GUEST_PATH } from '@/guest/guest-standalone';

export type PublicBootstrapRoute =
  | { kind: 'oauth-callback' }
  | { kind: 'signup' }
  | { kind: 'verify' }
  | { kind: 'invite-accept' }
  | { kind: 'signing-return'; outcome: SigningReturnOutcome }
  | { kind: 'document-signature-return'; outcome: DocumentSignatureReturnOutcome }
  | { kind: 'guest' };

/**
 * Every path `bootstrap()` MUST handle before — and without — Keycloak (ADR-001). Pulled out as a
 * pure function (T07) so it is testable on its own: calling it has no side effects (no React root,
 * no network, no DOM write), which lets a test prove "this pathname resolves to this route, and
 * only this one" without mounting the app — something a test of `bootstrap()` itself, which DOES
 * have those side effects once a route actually runs, could not do.
 *
 * Order matches `bootstrap()`'s own dispatch; see `main.tsx` for why each path must run before
 * Keycloak.
 */
export function resolvePublicBootstrapRoute(pathname: string): PublicBootstrapRoute | null {
  // A provider's post-consent redirect lands on `/oauth/callback`; gating it behind login-required
  // coupled a third-party callback to our auth server's uptime (a Keycloak 502 stranded the final
  // hop of an otherwise-completed connection).
  if (pathname === OAUTH_CALLBACK_PATH) return { kind: 'oauth-callback' };

  // The app uses onLoad:'login-required', which redirects before any route renders; this path is
  // intercepted before initKeycloak() so the registration page is reachable without an existing
  // session.
  if (pathname === SIGNUP_PATH || pathname.startsWith(SIGNUP_PATH + '/')) return { kind: 'signup' };

  // The recipient is not yet authenticated; gating /verify behind login-required would redirect
  // them to Keycloak login before they can confirm their address, breaking the flow.
  if (pathname === VERIFY_PATH) return { kind: 'verify' };

  // The sharpest version of the same reason as /verify: the recipient has NO credentials yet, so
  // login-required sent them to a sign-in form they could not possibly complete (reported from
  // production, 2026-09-23).
  if (pathname === INVITE_ACCEPT_PATH) return { kind: 'invite-accept' };

  // signNow navigates the signing iframe itself to these paths (D-11); the authenticated app
  // redirected that frame to Keycloak, which cannot render in a frame.
  const signingReturnOutcome = parseSigningReturnPath(pathname);
  if (signingReturnOutcome) return { kind: 'signing-return', outcome: signingReturnOutcome };

  // The external signer of a document-signature request (a client with no Entite login) lands here
  // from the provider's redirect; login-required sent them to a sign-in form they cannot complete,
  // and a router miss rendered "Not Found" inside the app shell (reported from dev, 2026-10-29).
  const documentSignatureOutcome = parseDocumentSignatureReturnPath(pathname);
  if (documentSignatureOutcome) {
    return { kind: 'document-signature-return', outcome: documentSignatureOutcome };
  }

  // The guest never authenticates with Keycloak at all (ADR-001 Amendment 1); `./guest/capture-
  // fragment` (imported above `./instrument`, before any other module evaluates) has already
  // captured and stripped the `#<token>` fragment.
  if (pathname === GUEST_PATH) return { kind: 'guest' };

  return null;
}
